<template>
  <layout
    title="Trusted private authentication for banking"
    :transparent="true"
    class="overflow-x-hidden"
  >
    <highlightSection
      backgroundImage="/storage/images/industries/hospitality/guest_id_verification.jpg"
      altImage="Guest check-in and ID verification"
      title="Streamlined guest check-in and ID verification for Hospitality"
      subtitle="Ensure operational efficiency, protect guests’ data, and eliminate check-in friction."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_Hospitality.pdf"
    ></highlightSection>

    <EndToEnd
      :endToEndData="endToEnd"
      title="Deliver excellence from the moment guests check in"
    ></EndToEnd>

    <FeatureHighlightSection :features="features" title="The ultimate solution for guest ID verification"/>
    <div class="pt-20">
      <cta
        title="Get started"
        text="Are you still waisting resources on manual check-in?"
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <div
      class="mt-20 flex md:flex-row flex-col items-center justify-center w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-xl gap-16"
    >
      <div
        class="flex justify-center flex-col max-w-md md:text-left text-center"
      >
        <h3 class="text-3xl font-bold">
          Deliver 5-star experiences from the first moment
        </h3>
        <p class="mt-6">
          Meet your customers' expectations with a fully digital, smooth, and
          secure check-in process designed to protect their privacy.
        </p>
      </div>
      <rowCardsSection :elements="elements" :hoverAnimation="false" class="max-w-md" />
    </div>

    <partners />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['hospitality']"
    />

    <div class="mt-20">
      <cta
        title="Talk to our experts"
        text="Your competitors are already ahead. Are you ready?"
        buttonText="Contact us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const EndToEnd = () =>
  import(
    /* webpackChunkName: "ProductsInfoCards" */ "../../Components/Layout/UseCases/EndToEndSection.vue"
  );

const FeatureHighlightSection = () =>
  import(
    /* webpackChunkName: "FeatureHighlightSection" */ "../../Components/Layout/UseCases/FeatureHighlightSection.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const rowCardsSection = () =>
  import(
    /* webpackChunkName: "rowCardsSection" */ "../../Components/Layout/rowCardsSection.vue"
  );

const partners = () =>
  import(
    /* webpackChunkName: "partners" */ "../../Components/Layout/HomePage/partners.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

export default {
  props: {
    errors: Object,
  },
  components: {
    layout,
    highlightSection,
    EndToEnd,
    FeatureHighlightSection,
    cta,
    rowCardsSection,
    partners,
    latestPosts,
  },
  data() {
    return {
      endToEnd: [
        {
          title: "Online check-in",
          iconPath: "/storage/images/icons/calendar-check.svg",
          description: ["Remote check-in", "ID verification"],
        },
        {
          title: "Front desk",
          iconPath: "/storage/images/icons/device-imac-check.svg",
          description: ["OCR", "PMS integration", "Loyalty app login"],
        },
        {
          title: "Room access",
          iconPath: "/storage/images/icons/key.svg",
          description: ["Keyless room access"],
        },
        {
          title: "Facility access",
          iconPath: "/storage/images/icons/heart-handshake.svg",
          description: ["Breakfast lounge", "SPA", "Locker"],
        },
      ],
      elements: [
        {
          title: `Identity Verification`,
          text: `With YouID, you can integrate identity verification flows into your business processes in just minutes.`,
          seeMoreUrl: `/products/youID`,
          imageSrc: `/storage/images/home/icons/identity_verification.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
      ],
      features: [
        {
          title: "Ensure operational efficiency",
          description:
            "Verify over 15,000 document types, ensuring accurate data extraction into the property management system.",
          image:
            "/storage/images/industries/hospitality/guest_id_verification.png",
        },
        {
          title: "Protect guest sensitive data",
          description:
            "Built on zero-trust principles to protect guest data at every step. No sensitive data or images are stored or shared over the network.",
          image:
            "/storage/images/industries/hospitality/guest_id_management.png",
        },
        {
          title: "Eliminate check-in friction",
          description:
            "Seamlessly align digital ID verification with regulations and property needs—without disrupting the guest experience.",
          image:
            "/storage/images/industries/hospitality/guest_remote_checkin.png",
        },
      ],
    };
  },
};
</script>
